import type {AgentInfo, Session} from '../types/session'
import type {HydratedAPITask} from '../types/api-task'
import type {Icon} from '@primer/octicons-react'
import {CopilotIcon, TerminalIcon, VscodeIcon} from '@primer/octicons-react'

/**
 * Props passed to the chat input slot component.
 * Kept minimal — each implementation can accept additional props via its own interface,
 * but this is the contract the shell component programs against.
 */
export interface ChatInputSlotProps {
  sessionId: string
  session: Session
  task?: HydratedAPITask | null
  onCommandSent?: () => void
  cliRemoteNoticeDismissed?: boolean
}

/**
 * Props passed to the leading icon slot component.
 */
export interface LeadingIconSlotProps {
  state?: string
  prState?: string
  prInMergeQueue?: boolean
  isArchived?: boolean
  isOptimistic?: boolean
  session?: Session | null
  staleAt?: string
  dataUpdatedAt?: number
  sharingStatus?: 'unshared' | 'shared'
}

/**
 * Component slots for agent-type-specific rendering.
 * Use `null` to indicate "don't render this slot" (e.g., code review agents have no ChatInput).
 */
export interface AgentProfileSlots {
  /** Icon shown in task lists and headers. When provided, overrides the default state-based icon logic. */
  LeadingIcon: React.ComponentType<LeadingIconSlotProps> | null
  /** Chat input component shown at the bottom of the task view. null = no input (e.g. code review). */
  ChatInput: React.ComponentType<ChatInputSlotProps> | null
  /** Component shown when a session is in_progress but has no logs yet. null = use default behavior. */
  EmptyState: React.ComponentType | null
}

/**
 * A complete agent behavior profile. Controls both simple boolean flags
 * and complex rendering via component slots.
 *
 * Adding a new agent type = create a Partial<AgentProfile> with only the overrides.
 * Unknown agents get DEFAULT_PROFILE automatically.
 */
export interface AgentProfile {
  /** Unique slug for this agent profile (e.g., 'copilot-developer-cli') */
  slug: string
  /** Human-readable name */
  displayName: string

  /**
   * Whether this agent type always has exactly one session per task.
   * When true, the per-session collapsible header is hidden (no need
   * for a divider) and session content is always expanded.
   */
  singleSessionAgent: boolean

  /**
   * Whether session data is private to the creator and cannot be meaningfully shared.
   * When true, the copy-link button is hidden in the task chat header.
   */
  privateSessions: boolean

  /**
   * Whether the agent provides its own intent/status messages as part of its event stream.
   * When true, synthetic UI-generated status messages ("Agent is booting up…",
   * "Agent is working…", "started a task/review") are suppressed.
   */
  agentProvidesIntent: boolean

  /**
   * Whether the session is a live interactive conversation (e.g. CLI).
   * Enables optimistic log entries for prompt responses and
   * display of raw message content for unmatched commands.
   */
  interactiveSession: boolean

  /** Whether to enable sub-agent log grouping (requires feature flag too) */
  enableAgentGrouping: boolean

  /** Label shown as secondary text in task lists (e.g. "Remote session"). null = use default status text. */
  secondaryLabel: string | null

  /**
   * Hardcoded agent info. When non-null, `useAgentInfo` can skip the server fetch.
   * When null, agent info is fetched from the monolith as usual.
   */
  agentInfo: AgentInfo | null

  slots: AgentProfileSlots
}

/**
 * The default agent profile. Represents standard behavior for the Copilot Cloud Agent
 * and serves as the fallback for unknown/third-party agent types.
 *
 * All boolean flags default to the "standard" behavior. Component slots default to null,
 * meaning the standard rendering path is used (no override).
 */
export const DEFAULT_PROFILE: AgentProfile = {
  slug: 'default',
  displayName: 'Agent',

  singleSessionAgent: false,

  privateSessions: false,

  agentProvidesIntent: false,
  interactiveSession: false,

  enableAgentGrouping: false,

  secondaryLabel: null,

  agentInfo: null,

  slots: {
    LeadingIcon: null,
    ChatInput: null, // null here means "use the default input component" — resolved by the consumer
    EmptyState: null,
  },
}

/**
 * Merges a partial profile with the default, correctly deep-merging the `slots` object.
 */
export function mergeWithDefault(overrides: Partial<AgentProfile>): AgentProfile {
  return {
    ...DEFAULT_PROFILE,
    ...overrides,
    slots: {
      ...DEFAULT_PROFILE.slots,
      ...overrides.slots,
    },
  }
}

/**
 * Data-only registry of hardcoded agent info keyed by slug.
 * Used by `useAgentInfo` to skip server fetches for known agents.
 * Kept separate from the full profile registry to avoid circular
 * dependencies (profiles import components, which eventually import
 * hooks that need agent info).
 */
export const AGENT_INFO_REGISTRY: Record<string, AgentInfo> = {
  'copilot-developer-cli': {
    display_name: 'Copilot CLI',
    slug: 'copilot-developer-cli',
    avatar_url: '',
    doc_url: '',
  },
  'vscode-chat': {
    display_name: 'Copilot in VS Code',
    slug: 'vscode-chat',
    avatar_url: '',
    doc_url: '',
  },
}

/**
 * Look up hardcoded agent info by slug. Returns null if the slug is unknown.
 */
export function getAgentInfoBySlug(slug?: string): AgentInfo | null {
  if (slug && AGENT_INFO_REGISTRY[slug]) {
    return AGENT_INFO_REGISTRY[slug]
  }
  return null
}

/**
 * Data-only registry of filter icons keyed by agent slug.
 * Used by filter option builders to render the correct icon without
 * hardcoding slug-to-icon mappings outside the agent-profile module.
 */
export const FILTER_ICON_REGISTRY: Record<string, Icon> = {
  'copilot-developer-cli': TerminalIcon,
  'vscode-chat': VscodeIcon,
}

/**
 * Look up the filter icon for a given agent slug.
 * Returns CopilotIcon for unknown or unregistered slugs.
 */
export function getFilterIconForSlug(slug?: string): Icon {
  if (slug && FILTER_ICON_REGISTRY[slug]) {
    return FILTER_ICON_REGISTRY[slug]
  }
  return CopilotIcon
}
